import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { AccountMembership, APIService, CreateProductServiceInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { AiProduct } from '../ai-workspace-setup.models';
import { fetchAllProductServices } from 'app/shared/pagination/paginated-api-queries';

@Component({
  selector: 'ws-products',
  templateUrl: './ws-products.component.html',
  imports: [FormsModule, NgClass, MatButton]
})
export class WsProductsComponent implements OnInit, OnChanges {
  @Input() suggestions: AiProduct[];
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Output() completed = new EventEmitter<void>();
  @Output() feedbackRequested = new EventEmitter<string>();

  products: AiProduct[] = [];
  isSaving = false;
  existingProducts: string[] = [];
  feedback = '';
  newProductName = '';
  newProductType: 'PRODUCT' | 'SERVICE' = 'PRODUCT';

  constructor(
    private api: APIService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.initProducts();
    if (!this.testMode) this.checkExistingData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions'] && this.suggestions) {
      this.initProducts();
    }
  }

  private initProducts() {
    this.products = (this.suggestions || []).map(p => ({
      ...p,
      selected: p.selected !== false,
    }));
  }

  private async checkExistingData() {
    if (!this.activeAccount?.accountId) return;
    try {
      const products = await fetchAllProductServices(this.api, {
        filter: { accountId: { eq: this.activeAccount.accountId } }
      });
      if (products.length) {
        this.existingProducts = products.map((p: any) => (p.name || '').toLowerCase());
        // Mark already-created products
        for (const p of this.products) {
          if (this.existingProducts.includes(p.name.toLowerCase())) {
            p.selected = false;
          }
        }
      }
    } catch (e) {
      console.error('Error checking existing products:', e);
    }
  }

  toggleProduct(product: AiProduct) {
    product.selected = !product.selected;
  }

  removeProduct(product: AiProduct) {
    this.products = this.products.filter(p => p !== product);
  }

  addProduct() {
    if (!this.newProductName.trim()) return;
    this.products.push({
      name: this.newProductName.trim(),
      type: this.newProductType,
      selected: true,
    });
    this.newProductName = '';
  }

  isAlreadyCreated(product: AiProduct): boolean {
    return this.existingProducts.includes(product.name.toLowerCase());
  }

  async save() {
    if (this.isSaving) return;
    const selected = this.products.filter(p => p.selected && !this.isAlreadyCreated(p));

    if (this.testMode) {
      this.toastr.success(`Test mode: ${selected.length} product(s) saved`);
      this.completed.emit();
      return;
    }

    this.isSaving = true;

    try {
      for (const p of selected) {
        const input = <CreateProductServiceInput>{
          accountId: this.activeAccount.accountId,
          name: p.name.trim(),
          type: p.type,
          category: p.category || undefined,
        };
        await this.api.CreateProductService(input);
      }

      if (selected.length > 0) {
        this.toastr.success(`${selected.length} product(s) created`);
      } else {
        this.toastr.info('No new products to create');
      }
      this.completed.emit();
    } catch (e: any) {
      console.error('Error creating products:', e);
      this.toastr.error(e?.errors?.[0]?.message || 'Failed to save');
    } finally {
      this.isSaving = false;
    }
  }

  submitFeedback() {
    if (this.feedback.trim()) {
      this.feedbackRequested.emit(this.feedback.trim());
      this.feedback = '';
    }
  }
}
